import { useReactiveVar } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import SubtitlesOutlinedIcon from '@mui/icons-material/SubtitlesOutlined';
import { Divider, IconButton, Link, Popover, Tooltip, Typography } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import ScopeSelector from 'src/components/Library/ScopeSelector';
import { getSearchableCharCount } from 'src/helpers/searchHelper';
import viewStateVar from 'src/store/viewState';
import type { BaseSearchParams } from 'src/utils/searchSchemas';
import styles from './SearchAppBarStyles';

const SEARCH_DEBOUNCE_DELAY = 400;
const MIN_SEARCHABLE_CHARS = 3;

interface SearchOptions {
  resetPagination: boolean;
  bypassMinChars?: boolean;
}

interface SearchAppBarProps {
  /** Optional placeholder override for the search input. */
  placeholder?: string;
}

export default function SearchAppBar({
  placeholder = 'Search assets, galleries, transcripts...',
}: SearchAppBarProps = {}): React.ReactElement {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as BaseSearchParams;
  const classes = styles();
  const [helpAnchorEl, setHelpAnchorEl] = useState<HTMLElement | null>(null);
  const { searchTerm } = useReactiveVar(viewStateVar);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMount = useRef<boolean>(true);
  const includeTranscript = search.transcript === true;

  const toggleTranscript = (): void => {
    navigate({
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        transcript: !includeTranscript,
        page: 1,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
      })) as any,
    });
  };

  useEffect(() => {
    const urlTerm = search.term || '';
    const currentTerm = viewStateVar().searchTerm;
    if (currentTerm.trim() !== urlTerm) {
      viewStateVar({
        ...viewStateVar(),
        searchTerm: urlTerm,
      });
    }
  }, [search.term]);

  const executeSearch = useCallback(
    (term: string, { resetPagination }: SearchOptions = { resetPagination: true }): void => {
      navigate({
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          term: term?.trim() || undefined,
          page: resetPagination ? 1 : (prev.page as number | undefined),
          // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
        })) as any,
      });
    },
    [navigate],
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (searchTerm === '') {
      if (search.term) {
        executeSearch('', { resetPagination: true });
      }
      return;
    }

    if (getSearchableCharCount(searchTerm) < MIN_SEARCHABLE_CHARS) {
      if (search.term && search.term !== searchTerm.trim()) {
        executeSearch('', { resetPagination: true });
      }
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      executeSearch(searchTerm, { resetPagination: true });
    }, SEARCH_DEBOUNCE_DELAY);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm, executeSearch, search.term]);

  function handleSearch({ resetPagination, bypassMinChars }: SearchOptions = { resetPagination: true }): void {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    const charCount = getSearchableCharCount(searchTerm);
    const minRequired = bypassMinChars ? 1 : MIN_SEARCHABLE_CHARS;
    if (searchTerm && charCount < minRequired) {
      if (search.term) {
        executeSearch('', { resetPagination });
      }
      return;
    }
    executeSearch(searchTerm, { resetPagination });
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    viewStateVar({
      ...viewStateVar(),
      searchTerm: e.target.value,
    });
  };

  const handleClear = (): void => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    viewStateVar({
      ...viewStateVar(),
      searchTerm: '',
    });
  };

  return (
    <div className={classes.root}>
      <div className={classes.search}>
        <ScopeSelector />
        <InputBase
          onKeyDown={(k) => k.key === 'Enter' && handleSearch({ resetPagination: true, bypassMinChars: true })}
          placeholder={placeholder}
          id="cyp-main-search-input"
          onChange={onChange}
          value={searchTerm}
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
          }}
          inputProps={{ 'aria-label': 'search' }}
          startAdornment={
            <InputAdornment position="start" style={{ marginRight: -4, marginLeft: 4 }}>
              <Tooltip title="Search" placement="bottom" arrow>
                <IconButton
                  onClick={() => handleSearch({ resetPagination: true, bypassMinChars: true })}
                  aria-label="Search"
                  size="small"
                  sx={{ padding: '4px' }}
                >
                  <SearchIcon style={{ color: 'inherit', fontSize: 18, display: 'block' }} />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          }
          endAdornment={
            <InputAdornment
              position="end"
              style={{ marginRight: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <CloseIcon
                style={{
                  cursor: searchTerm ? 'pointer' : 'default',
                  fontSize: '18px',
                  visibility: searchTerm ? 'visible' : 'hidden',
                }}
                onClick={searchTerm ? handleClear : undefined}
                aria-label="Clear search"
              />
              <Tooltip title="Search help" placement="bottom" arrow>
                <IconButton
                  onClick={(e) => setHelpAnchorEl(e.currentTarget)}
                  aria-label="Search help"
                  size="small"
                  sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                  <HelpOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <span className={classes.adornmentSeparator} />
              <Tooltip
                title={includeTranscript ? 'Transcripts included' : 'Include transcripts'}
                placement="bottom"
                arrow
              >
                <IconButton
                  onClick={toggleTranscript}
                  aria-label="Toggle transcript search"
                  size="small"
                  sx={{
                    color: includeTranscript ? 'secondary.main' : undefined,
                    opacity: includeTranscript ? 1 : 0.5,
                    '&:hover': { opacity: 1 },
                  }}
                >
                  <SubtitlesOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          }
        />
        <Popover
          open={Boolean(helpAnchorEl)}
          anchorEl={helpAnchorEl}
          onClose={() => setHelpAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              className: classes.helpPopover,
            },
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Search Tips
          </Typography>
          <Typography variant="caption" sx={{ mb: 1.5, display: 'block', opacity: 0.6 }}>
            Wildcards are automatic — <code className={classes.helpCode}>sunset</code> matches sunset, sunsets,
            sunsetbeach, etc.
          </Typography>
          <table className={classes.helpTable}>
            <tbody>
              <tr>
                <td>
                  <code className={classes.helpCode}>interview drone</code>
                </td>
                <td>both words</td>
              </tr>
              <tr>
                <td>
                  <code className={classes.helpCode}>interview OR drone</code>
                </td>
                <td>either word</td>
              </tr>
              <tr>
                <td>
                  <code className={classes.helpCode}>interview NOT drone</code>
                </td>
                <td>exclude second</td>
              </tr>
              <tr>
                <td>
                  <code className={classes.helpCode}>"behind the scenes"</code>
                </td>
                <td>exact phrase</td>
              </tr>
              <tr>
                <td>
                  <code className={classes.helpCode}>-raw</code>
                </td>
                <td>exclude term</td>
              </tr>
              <tr>
                <td>
                  <code className={classes.helpCode}>*lapse</code>
                </td>
                <td>ends with</td>
              </tr>
              <tr>
                <td>
                  <code className={classes.helpCode}>name:sunset</code>
                </td>
                <td>search by field name</td>
              </tr>
              <tr>
                <td>
                  <code className={classes.helpCode}>metadata_fields.genre:comedy</code>
                </td>
                <td>search by metadata slug</td>
              </tr>
            </tbody>
          </table>
          <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.5 }}>
            Use <SubtitlesOutlinedIcon sx={{ fontSize: 13, verticalAlign: 'middle', mx: 0.25 }} /> to include
            transcripts in results.
          </Typography>
          <Divider sx={{ my: 1.5, opacity: 0.3 }} />
          <Link
            href="https://docs.northshoreautomation.com/space/NKB/1855062018/Akomi+Advanced+Search"
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            Advanced Search guide
            <OpenInNewIcon sx={{ fontSize: 12 }} />
          </Link>
        </Popover>
      </div>
    </div>
  );
}
